/**
 * Admin Category Edit Page: create/edit event category.
 * Native mobile app style (iOS Settings-like).
 */

import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Layout } from "../../components/layout/index.ts";
import { PageLoader } from "../../components/PageLoader.tsx";
import { IconAlertTriangle, IconCheck } from "../../components/Icons.tsx";
import {
  getTelegram,
  haptic,
  safeShowAlert,
  safeShowConfirm,
} from "../../lib/telegram.ts";
import { getErrorMessage } from "../../lib/graphql.ts";
import {
  useAdminCategory,
  useCategoriesList,
  useCreateCategory,
  useUpdateCategory,
  useDeleteCategory,
  type AdminCategoryDetail,
  type CategoryOption,
} from "@/features/admin/hooks";

// Color palette
const COLORS = [
  "#EF4444",
  "#F59E0B",
  "#10B981",
  "#14B8A6",
  "#3B82F6",
  "#6366F1",
  "#8B5CF6",
  "#EC4899",
  "#FCA5A5",
  "#FCD34D",
  "#6EE7B7",
  "#5EEAD4",
  "#93C5FD",
  "#A5B4FC",
  "#C4B5FD",
  "#F9A8D4",
  "#F97316",
  "#84CC16",
  "#22C55E",
  "#06B6D4",
  "#0EA5E9",
  "#A855F7",
  "#D946EF",
  "#64748B",
];

export function AdminCategoryEditPage() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const tg = getTelegram();

  const isNew = categoryId === "new";

  // URQL hooks
  const {
    category: loadedCategory,
    allCategories: loadedAllCategories,
    isLoading: isLoadingCategory,
    error: categoryError,
    refetch: refetchCategory,
  } = useAdminCategory(isNew ? undefined : categoryId, { pause: isNew });

  const { categories: newCategories } = useCategoriesList({ pause: !isNew });

  const { createCategory, isLoading: isCreating } = useCreateCategory();
  const { updateCategory, isLoading: isUpdating } = useUpdateCategory();
  const { deleteCategory, isLoading: isDeleting } = useDeleteCategory();

  const isLoading = isLoadingCategory && !isNew;
  const isSaving = isCreating || isUpdating;
  const isHiding = isDeleting;
  const error = categoryError?.message ?? null;

  // Derive allCategories based on mode
  const allCategories: CategoryOption[] = isNew
    ? newCategories
    : loadedAllCategories.filter((c) => c.id !== loadedCategory?.id);

  // Store initial loaded category for reference
  const [category, setCategory] = useState<AdminCategoryDetail | null>(null);

  // Form state
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState<string | null>(null);
  const [parentId, setParentId] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState("0");
  const [isActive, setIsActive] = useState(true);
  const [formInitialized, setFormInitialized] = useState(false);

  // Parent picker
  const [showParentPicker, setShowParentPicker] = useState(false);

  // Initialize form when category loads
  useEffect(() => {
    if (loadedCategory && !formInitialized) {
      setCategory(loadedCategory);
      setCode(loadedCategory.code);
      setName(loadedCategory.name);
      setDescription(loadedCategory.description ?? "");
      setColor(loadedCategory.color);
      setParentId(loadedCategory.parent?.id ?? null);
      setSortOrder(String(loadedCategory.sortOrder));
      setIsActive(loadedCategory.isActive);
      setFormInitialized(true);
    }
  }, [loadedCategory, formInitialized]);

  // BackButton
  useEffect(() => {
    if (!tg) return;
    tg.BackButton.show();
    const handleBack = () => {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate("/admin/categories");
      }
    };
    tg.BackButton.onClick(handleBack);
    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate]);

  // Auto-generate code
  const handleNameChange = (value: string) => {
    setName(value);
    if (isNew && !code) {
      setCode(
        value
          .toLowerCase()
          .replace(/[^a-zа-яё0-9\s-]/gi, "")
          .replace(/\s+/g, "-")
          .replace(/-+/g, "-")
          .slice(0, 30),
      );
    }
  };

  // Save
  const handleSave = async () => {
    if (!code.trim()) return safeShowAlert("Введите код");
    if (!name.trim()) return safeShowAlert("Введите название");

    try {
      if (isNew) {
        const newId = await createCategory({
          code: code.trim().toLowerCase(),
          name: name.trim(),
          description: description.trim() || null,
          color,
          parentId,
          sortOrder: parseInt(sortOrder, 10) || 0,
          isActive,
        });
        haptic("success");
        navigate(`/admin/categories/${newId}`, {
          replace: true,
        });
      } else {
        if (!categoryId) {
          safeShowAlert("ID категории не найден");
          return;
        }
        await updateCategory(categoryId, {
          name: name.trim(),
          description: description.trim() || null,
          color,
          parentId,
          sortOrder: parseInt(sortOrder, 10) || 0,
          isActive,
        });
        haptic("success");
        safeShowAlert("Сохранено");
        refetchCategory();
      }
    } catch (err) {
      haptic("error");
      safeShowAlert(getErrorMessage(err, "Ошибка сохранения"));
    }
  };

  // Hide (soft delete)
  const handleHide = async () => {
    if (isNew || !category || !categoryId) return;

    const confirmed = await new Promise<boolean>((resolve) =>
      safeShowConfirm("Скрыть категорию? Данные сохранятся.", resolve),
    );
    if (!confirmed) return;

    try {
      await deleteCategory(categoryId);
      haptic("success");
      navigate("/admin/categories");
    } catch (err) {
      haptic("error");
      safeShowAlert(getErrorMessage(err, "Ошибка удаления"));
    }
  };

  const availableParents = allCategories.filter((c) =>
    isNew ? true : c.id !== categoryId,
  );
  const selectedParent = availableParents.find((p) => p.id === parentId);

  if (isLoading) return <PageLoader message="Загрузка..." />;

  if (error) {
    return (
      <Layout>
        <div className="card p-5 text-center">
          <IconAlertTriangle className="w-12 h-12 mx-auto mb-3 text-amber-500" />
          <h2 className="text-lg font-bold text-slate-900 mb-3">{error}</h2>
          <button
            onClick={() => navigate("/admin/categories")}
            className="btn-primary"
          >
            К списку
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Preview header */}
      <div className="flex items-center gap-4 mb-6">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-sm"
          style={{ backgroundColor: color || "#e2e8f0" }}
        >
          <svg
            className="w-7 h-7 text-white"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9.568 3H5.25A2.25 2.25 0 0 0 3 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 0 0 5.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 0 0 9.568 3Z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 6h.008v.008H6V6Z"
            />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-slate-900 truncate">
            {name || (isNew ? "Новая категория" : "Редактирование")}
          </h1>
          <p className="text-[13px] text-slate-500 font-mono">
            {code || "код-категории"}
            {!isNew && category && ` · ${category.eventCount} событий`}
          </p>
        </div>
      </div>

      {/* Main info group */}
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2 px-1">
        Основное
      </p>
      <div className="card overflow-hidden mb-5">
        {/* Name */}
        <div className="flex items-center min-h-[52px] px-4 border-b border-slate-100">
          <span className="text-[15px] text-slate-500 w-24 flex-shrink-0">
            Название
          </span>
          <input
            type="text"
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="Молодёжь"
            className="flex-1 text-[15px] text-slate-900 bg-transparent outline-none text-right"
          />
        </div>

        {/* Code */}
        <div className="flex items-center min-h-[52px] px-4 border-b border-slate-100">
          <span className="text-[15px] text-slate-500 w-24 flex-shrink-0">
            Код
          </span>
          {isNew ? (
            <input
              type="text"
              value={code}
              onChange={(e) =>
                setCode(e.target.value.toLowerCase().replace(/\s/g, "-"))
              }
              placeholder="youth"
              className="flex-1 text-[15px] text-slate-900 font-mono bg-transparent outline-none text-right"
            />
          ) : (
            <span className="flex-1 text-[15px] text-slate-400 font-mono text-right">
              {code}
            </span>
          )}
        </div>

        {/* Description */}
        <div className="px-4 py-3">
          <span className="text-[15px] text-slate-500 block mb-2">
            Описание
          </span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Краткое описание..."
            rows={2}
            className="w-full text-[15px] text-slate-900 bg-slate-50 rounded-lg px-3 py-2 outline-none resize-none"
          />
        </div>
      </div>

      {/* Code warning */}
      {isNew && (
        <p className="text-[11px] text-amber-600 mb-5 px-1 flex items-center gap-1">
          <IconAlertTriangle className="w-3 h-3" />
          Код нельзя изменить после создания
        </p>
      )}

      {/* Color */}
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2 px-1">
        Цвет
      </p>
      <div className="card p-3 mb-5">
        <div className="grid grid-cols-8 gap-2">
          {COLORS.map((c) => {
            const isSelected = color?.toUpperCase() === c.toUpperCase();
            return (
              <button
                key={c}
                type="button"
                onClick={() => {
                  haptic("light");
                  setColor(isSelected ? null : c);
                }}
                className={`aspect-square rounded-xl transition-all ${
                  isSelected
                    ? "ring-2 ring-offset-2 ring-slate-900 scale-110"
                    : "active:scale-90"
                }`}
                style={{ backgroundColor: c }}
              >
                {isSelected && (
                  <IconCheck className="w-4 h-4 text-white mx-auto" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Settings group */}
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2 px-1">
        Настройки
      </p>
      <div className="card overflow-hidden mb-5">
        {/* Parent */}
        <button
          type="button"
          onClick={() => {
            haptic("light");
            setShowParentPicker(!showParentPicker);
          }}
          className="w-full flex items-center justify-between min-h-[52px] px-4 active:bg-slate-50 transition-colors"
        >
          <span className="text-[15px] text-slate-900">Родитель</span>
          <div className="flex items-center gap-2">
            <span className="text-[15px] text-slate-500">
              {selectedParent?.name || "Нет"}
            </span>
            <svg
              className={`w-4 h-4 text-slate-400 transition-transform ${showParentPicker ? "rotate-180" : ""}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </button>

        {/* Expandable parent picker */}
        {showParentPicker && (
          <div className="border-t border-slate-100 bg-slate-50">
            <button
              type="button"
              onClick={() => {
                haptic("light");
                setParentId(null);
                setShowParentPicker(false);
              }}
              className={`w-full flex items-center justify-between min-h-[44px] px-4 active:bg-slate-100 ${
                !parentId ? "text-[var(--color-accent)]" : "text-slate-700"
              }`}
            >
              <span className="text-[14px]">Нет (корневая)</span>
              {!parentId && <IconCheck className="w-4 h-4" />}
            </button>
            {availableParents.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => {
                  haptic("light");
                  setParentId(p.id);
                  setShowParentPicker(false);
                }}
                className={`w-full flex items-center justify-between min-h-[44px] px-4 border-t border-slate-200/50 active:bg-slate-100 ${
                  parentId === p.id
                    ? "text-[var(--color-accent)]"
                    : "text-slate-700"
                }`}
              >
                <span className="text-[14px]">{p.name}</span>
                {parentId === p.id && <IconCheck className="w-4 h-4" />}
              </button>
            ))}
          </div>
        )}

        {/* Sort order */}
        <div className="flex items-center justify-between min-h-[52px] px-4 border-t border-slate-100">
          <span className="text-[15px] text-slate-900">Порядок</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                haptic("light");
                setSortOrder(String(Math.max(0, parseInt(sortOrder, 10) - 1)));
              }}
              className="w-8 h-8 rounded-lg bg-slate-100 text-slate-600 text-lg font-medium active:bg-slate-200"
            >
              −
            </button>
            <span className="w-8 text-center text-[15px] font-medium text-slate-900">
              {sortOrder}
            </span>
            <button
              type="button"
              onClick={() => {
                haptic("light");
                setSortOrder(String(parseInt(sortOrder, 10) + 1));
              }}
              className="w-8 h-8 rounded-lg bg-slate-100 text-slate-600 text-lg font-medium active:bg-slate-200"
            >
              +
            </button>
          </div>
        </div>

        {/* Active toggle */}
        <div className="flex items-center justify-between min-h-[52px] px-4 border-t border-slate-100">
          <span className="text-[15px] text-slate-900">Активна</span>
          <button
            type="button"
            onClick={() => {
              haptic("light");
              setIsActive(!isActive);
            }}
            className={`relative w-12 h-7 rounded-full transition-colors ${
              isActive ? "bg-green-500" : "bg-slate-200"
            }`}
          >
            <span
              className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${
                isActive ? "left-5" : "left-0.5"
              }`}
            />
          </button>
        </div>
      </div>

      {/* Save button */}
      <button
        type="button"
        onClick={handleSave}
        disabled={isSaving || isHiding}
        className="btn-primary w-full py-4 text-[15px] font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {isSaving ? (
          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        ) : (
          <IconCheck className="w-5 h-5" />
        )}
        {isSaving ? "Сохранение..." : isNew ? "Создать" : "Сохранить"}
      </button>

      {/* Hide button */}
      {!isNew && isActive && (
        <button
          type="button"
          onClick={handleHide}
          disabled={isSaving || isHiding}
          className="w-full py-3 mt-2 text-red-500 text-[14px] font-medium active:text-red-600 disabled:opacity-50"
        >
          {isHiding ? "Скрытие..." : "Скрыть категорию"}
        </button>
      )}

      <div className="h-8" />
    </Layout>
  );
}
